import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Plus, GraduationCap, CalendarCheck, UserCheck, MessageCircle, Copy, Mail } from "lucide-react";

function Row({ icon: Icon, title, children, testid }) {
  return (
    <div className="flex items-start gap-3 py-3" style={{ borderTop: "1px solid var(--border-subtle)" }} data-testid={testid}>
      <div className="shrink-0 flex items-center justify-center rounded-md mt-0.5" style={{ width: 30, height: 30, background: "var(--bg-base)", color: "var(--gold)" }}><Icon size={15} /></div>
      <div className="flex-1 min-w-0">
        <div className="text-[0.6rem] uppercase tracking-[0.18em] mb-1.5" style={{ color: "var(--muted)" }}>{title}</div>
        {children}
      </div>
    </div>
  );
}

function ClassesRow({ data, run, cfg }) {
  const [pick, setPick] = useState("");
  const inIds = new Set(data.next_steps_classes.map((c) => c.class_id));
  const available = data.options.classes.filter((c) => !inIds.has(c.id));
  return (
    <Row icon={GraduationCap} title="Next Steps class" testid="assign-classes">
      <div className="flex flex-wrap gap-1.5 mb-2">
        {data.next_steps_classes.length === 0 && <span className="text-xs italic" style={{ color: "var(--muted)" }}>Not in a class</span>}
        {data.next_steps_classes.map((c) => (
          <span key={c.class_id} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full" style={{ background: "var(--surface-hover)", color: "var(--ink)", border: "1px solid var(--border-subtle)" }} data-testid={`assign-class-chip-${c.class_id}`}>
            {c.class_name}
            <button onClick={() => run(() => api.delete(`/coord/directory/${data.guest_id}/next-steps-class/${c.class_id}`, cfg), "Removed from class.")} className="hover:text-[var(--terracotta)]" data-testid={`assign-class-remove-${c.class_id}`} aria-label="Remove from class"><X size={11} /></button>
          </span>
        ))}
      </div>
      {available.length > 0 && (
        <div className="flex gap-2">
          <select value={pick} onChange={(e) => setPick(e.target.value)} className="text-xs px-2 py-1.5 flex-1 min-w-0" data-testid="assign-class-select">
            <option value="">Choose a class…</option>
            {available.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button disabled={!pick} onClick={() => run(() => api.post(`/coord/directory/${data.guest_id}/next-steps-class`, { class_id: pick }, cfg), "Added to class.").then(() => setPick(""))} className="btn-ghost text-xs" style={{ padding: "0.35rem 0.8rem" }} data-testid="assign-class-add"><Plus size={12} /> Add</button>
        </div>
      )}
    </Row>
  );
}

function AttendanceRow({ data, run, cfg }) {
  const { enrolled, section_id } = data.attendance;
  const put = (body, msg) => run(() => api.put(`/coord/directory/${data.guest_id}/attendance`, body, cfg), msg);
  return (
    <Row icon={CalendarCheck} title="Attendance" testid="assign-attendance">
      <div className="flex items-center gap-2 flex-wrap">
        <select value={enrolled ? (section_id || "__none") : ""} onChange={(e) => {
          const v = e.target.value;
          if (!v) put({ enrolled: false }, "Removed from attendance.");
          else put({ enrolled: true, section_id: v === "__none" ? null : v }, "Attendance updated.");
        }} className="text-xs px-2 py-1.5 flex-1 min-w-0" data-testid="assign-attendance-select">
          <option value="">Not tracked in attendance</option>
          <option value="__none">In attendance — no section</option>
          {data.options.sections.map((s) => <option key={s.id} value={s.id}>Section: {s.name}</option>)}
        </select>
      </div>
    </Row>
  );
}

function TeacherRow({ data, run, cfg }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const t = data.teacher;
  if (t) {
    return (
      <Row icon={UserCheck} title="Teacher role" testid="assign-teacher-role">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <span className="text-sm" style={{ color: "var(--ink)" }} data-testid="assign-teacher-status">Teacher{t.has_login ? ` · portal login (${t.email})` : " · no portal login"}</span>
          <button onClick={() => window.confirm("Remove the teacher role? Their Teacher Portal access will be revoked.") && run(() => api.delete(`/coord/directory/${data.guest_id}/teacher`, cfg), "Teacher role removed.")} className="text-xs underline" style={{ color: "var(--terracotta)" }} data-testid="assign-teacher-remove">Remove teacher role</button>
        </div>
      </Row>
    );
  }
  return (
    <Row icon={UserCheck} title="Teacher role" testid="assign-teacher-role">
      {!open ? (
        <button onClick={() => setOpen(true)} className="btn-ghost text-xs" style={{ padding: "0.35rem 0.8rem" }} data-testid="assign-teacher-make"><Plus size={12} /> Make a teacher</button>
      ) : (
        <div className="space-y-2">
          <p className="text-[0.65rem]" style={{ color: "var(--muted)" }}>Optional: add an email + password to give them a Teacher Portal login.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input type="email" placeholder="Login email (optional)" value={email} onChange={(e) => setEmail(e.target.value)} className="text-xs px-2 py-1.5" data-testid="assign-teacher-email" />
            <input type="password" placeholder="Password, 8+ chars (optional)" value={password} onChange={(e) => setPassword(e.target.value)} className="text-xs px-2 py-1.5" data-testid="assign-teacher-password" />
          </div>
          <div className="flex gap-2">
            <button onClick={() => run(() => api.post(`/coord/directory/${data.guest_id}/teacher`, { email: email || null, password: password || null }, cfg), "Now a teacher.")} className="btn-primary text-xs" style={{ padding: "0.4rem 0.9rem" }} data-testid="assign-teacher-save">Save</button>
            <button onClick={() => setOpen(false)} className="btn-ghost text-xs" style={{ padding: "0.4rem 0.9rem" }}>Cancel</button>
          </div>
        </div>
      )}
    </Row>
  );
}

function SocialRow({ orgId, email }) {
  const [link, setLink] = useState("");
  const getLink = async () => {
    if (link) return link;
    const r = await api.get("/social/invite-code", { params: orgId ? { org_id: orgId } : {} });
    const l = `${window.location.origin}/social/join/${r.data.code}`;
    setLink(l);
    return l;
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(await getLink()); toast.success("Invite link copied."); }
    catch (err) { toast.error(err?.response?.data?.detail || "Couldn't get the invite link."); }
  };
  const mail = async () => {
    try {
      const l = await getLink();
      window.location.href = `mailto:${email}?subject=${encodeURIComponent("Join our church on thedisciple.social")}&body=${encodeURIComponent(`Join our church's feed on thedisciple.social. It's free:\n${l}`)}`;
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't get the invite link."); }
  };
  return (
    <Row icon={MessageCircle} title="thedisciple.social" testid="assign-social">
      <div className="flex gap-2 flex-wrap">
        <button onClick={copy} className="btn-ghost text-xs" style={{ padding: "0.35rem 0.8rem" }} data-testid="assign-social-copy"><Copy size={12} /> Copy invite link</button>
        {email && <button onClick={mail} className="btn-ghost text-xs" style={{ padding: "0.35rem 0.8rem" }} data-testid="assign-social-email"><Mail size={12} /> Email invite</button>}
      </div>
    </Row>
  );
}

export default function AssignmentsPanel({ guestId, orgId, onChanged, email }) {
  const [data, setData] = useState(null);
  const params = orgId ? { org_id: orgId } : {};

  useEffect(() => {
    api.get(`/coord/directory/${guestId}/assignments`, { params }).then((r) => setData(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestId, orgId]);

  const run = async (fn, msg) => {
    try {
      const r = await fn();
      setData(r.data);
      toast.success(msg);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update.");
    }
  };
  if (!data) return null;
  return (
    <section className="mb-8 p-4 rounded-md border" style={{ borderColor: "var(--border-subtle)" }} data-testid="guest-assignments-panel">
      <div className="flex items-center gap-2 mb-1">
        <h4 className="overline" style={{ color: "var(--gold)" }}>Assignments</h4>
        {data.supporter && <span className="text-[0.55rem] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-full" style={{ background: "rgba(230,184,92,0.15)", color: "var(--gold)" }} data-testid="assign-supporter-badge">Supporter</span>}
      </div>
      <p className="text-xs mb-1" style={{ color: "var(--muted)" }}>Add or remove this person from classes, attendance, and the teacher team.</p>
      <ClassesRow data={data} run={run} cfg={{ params }} />
      <AttendanceRow data={data} run={run} cfg={{ params }} />
      <TeacherRow data={data} run={run} cfg={{ params }} />
      <SocialRow orgId={orgId} email={email} />
    </section>
  );
}
